import {
	Container,
	createTheme,
	em,
	MantineTheme,
	NumberInput,
	rem,
	TextInput,
	DEFAULT_THEME,
	Button,
	Title,
	MantineThemeOverride,
	mergeMantineTheme,
} from "@mantine/core"
import {} from "@mantine/core"

const CONTAINER_SIZES: Record<string, number> = {
	xxs: 300,
	xs: 400,
	sm: 500,
	md: 600,
	lg: 700,
	xl: 806,
	xxl: 1160,
}

const themeOverride: MantineThemeOverride = createTheme({
	fontFamily: "Inter, sans-serif",
	fontSizes: {
		xs: rem(12),
		sm: rem(14),
		md: rem(16),
		lg: rem(18),
		xl: rem(20),
	},
	headings: {
		fontWeight: "500",
		sizes: {
			h1: {
				fontSize: rem(36),
				lineHeight: "1.3",
				fontWeight: "600",
			},
			h2: {
				fontSize: rem(24),
				lineHeight: "1.35",
				fontWeight: "700",
			},
		},
	},

	spacing: {
		xxs: rem(8),
		xs: rem(10),
		sm: rem(12),
		md: rem(16),
		lg: rem(20),
		xl: rem(32),
		xxl: rem(40),
		max: rem(60),
	},

	breakpoints: {
		xs: em(375),
		sm: em(768),
		md: em(1024),
		lg: em(1920),
	},
	radius: {
		xs: rem(4),
		md: rem(5),
		lg: rem(24),
		xl: "50%",
	},
	defaultRadius: "md",

	colors: {
		dark: [
			"#FAFAFA",
			"#F0F0F0",
			"#747474",
			"#636363",
			"#9D9D9D",
			"#666666",
			"#25282D",
			"#212121",
			"#000000b3",
			"#000",
		],
		blue: [
			"#EEF4FF",
			"#E2EBFF",
			"#D5E2FF",
			"#C8D9FF",
			"#9DB6FF",
			"#7F9FFF",
			"#6286FF",
			"#4791FF",
			"#3A74CC",
			"#2D5799",
		],
		red: [
			"#FFE8EA",
			"#FFD1D5",
			"#FFBAC0",
			"#FFA3AB",
			"#FF8C96",
			"#FF7581",
			"#DA0B25",
			"#C30920",
			"#AC081C",
			"#950718",
		],
		gray: [
			"#FAFAFA",
			"#F0F0F0",
			"#747474",
			"#636363",
			"#B4B8CE",
			"#4C4C4C",
			"#9D9D9D",
			"#666666",
			"#25282D",
			"#212121",
		],
		green: [
			"#16C706",
			"#dfeceb",
			"#c5d4d3",
			"#a8bcbb",
			"#8fa7a6",
			"#7e9a99",
			"#749492",
			"#61817f",
			"#527371",
			"#3f6463",
		],
		yellow: [
			"#fcf9e7",
			"#f5f1d7",
			"#e9e1b1",
			"#ddd188",
			"#d3c364",
			"#ccba4e",
			"#c9b541",
			"#b19f32",
			"#9e8d28",
			"#887a1b",
		],
		custom: ["", "", "", "", "", "", "", "", "", ""],
	},
	primaryColor: "dark",
	primaryShade: 6,
	cursorType: "pointer",
	components: {
		Input: {
			defaultProps: {
				h: rem(40),
				size: "md",
				fw: "500",
			},
		},
		PinInput: {
			defaultProps: {
				size: "md",
				radius: "md",
				placeholder: "0",
			},
		},
		InputLabel: {
			defaultProps: {
				fw: "400",
			},
		},
		InputError: {
			defaultProps: {
				c: "red.6",
			},
		},
		Button: Button.extend({
			styles: (theme, { variant }) => ({
				root: {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					height: "var(--button-height)",
					fontSize: theme.fontSizes.md,
					backgroundColor: theme.colors.custom[0] ? theme.colors.custom[0] : theme.colors.dark[9],
					fontWeight: 500,
					padding: "12px 36px",
					borderRadius: theme.radius.md,
					...(variant === "red" && {
						backgroundColor: theme.colors.red[6],
						color: theme.white,
						"&:hover": {
							backgroundColor: theme.colors.red[7],
						},
					}),

					...(variant === "gray" && {
						backgroundColor: theme.colors.dark[1],
						color: theme.colors.dark[6],
						"&:hover": {
							backgroundColor: theme.colors.dark[3],
						},
					}),

					...(variant === "white" && {
						backgroundColor: theme.white,
						color: theme.colors.dark[9],
						"&:hover": {
							backgroundColor: theme.colors.dark[3],
						},
					}),
					...(variant === "transparent" && {
						backgroundColor: "transparent",
						color: theme.colors.dark[9],
						"&:hover": {
							backgroundColor: "transparent",
						},
					}),
					...(variant === "outline" && {
						backgroundColor: "transparent",
						border: `px solid rgba(0, 0, 0, 0.30)`,
					}),
				},
			}),
			vars: (theme, { size }) => {
				if (size === "xs") {
					return {
						root: {
							"--button-height": rem(40),
							"--button-padding-x": rem(14),
							"--button-fz": rem(16),
						},
					}
				}

				if (size === "sm") {
					return {
						root: {
							"--button-height": rem(40),
							"--button-width": rem(40),
							"--button-padding-x": rem(0),
						},
					}
				}

				if (size === "md") {
					return {
						root: {
							"--button-height": rem(40),
							"--button-padding-x": rem(12),
							"--button-fz": theme.fontSizes.md,
						},
					}
				}

				if (size === "lg") {
					return {
						root: {
							"--button-height": rem(52),
							"--button-padding-x": rem(26),
							"--button-fz": theme.fontSizes.lg,
						},
					}
				}

				if (size === "xl") {
					return {
						root: {
							"--button-height": rem(60),
							"--button-padding-x": rem(32),
							"--button-fz": theme.fontSizes.xl,
						},
					}
				}

				return { root: {} }
			},
		}),
		Title: Title.extend({
			vars: (theme, { order }) => ({
				root: {
					...(order === 1 && {
						"--title-fw": "600",
						"--title-lh": "1.3",
						"--title-fz": rem(36),
						"--title-test": theme.colors.dark[6],
					}),
					...(order === 2 && {
						"--title-fw": "600",
						"--title-lh": "1.1",
						"--title-fz": rem(32),
					}),
					...(order === 3 && {
						"--title-fw": "700",
						"--title-lh": "1.2",
						"--title-fz": rem(20),
					}),
				},
			}),
		}),
		Container: Container.extend({
			vars: (_, { size, fluid }) => ({
				root: {
					"--container-size": fluid
						? "100%"
						: size !== undefined && size in CONTAINER_SIZES
						? rem(CONTAINER_SIZES[size])
						: rem(size),
				},
			}),
		}),
		NumberInput: NumberInput.extend({
			styles: theme => ({
				root: {
					width: "100%",
				},

				input: {
					height: "100%",
					fontWeight: 400,
					padding: `${rem(12)} ${rem(16)}`,

					"&:focus": {
						borderColor: theme.colors.red[6],
					},

					"&:hover": {
						borderColor: theme.colors.dark[3],
					},
				},
				vars: () => {
					return {
						root: {
							"--input-height": rem(40),
							"--input-fz": rem(16),
						},
					}
				},
			}),
		}),
		TextInput: TextInput.extend({
			styles: theme => ({
				root: {
					width: "100%",
				},

				label: {
					fontSize: rem(12),
					fontWeight: 500,
					color: theme.colors.dark[8],
				},

				input: {
					height: "100%",
					fontSize: rem(14),
					fontWeight: 400,
					borderRadius: theme.radius.md,
					border: `none`,
					padding: `${rem(12)} ${rem(16)}`,

					"&:focus": {
						borderColor: theme.colors.red[6],
					},

					"&:hover": {
						borderColor: theme.colors.dark[3],
					},
				},
				vars: () => {
					return {
						root: {
							"--input-height": rem(40),
							"--input-fz": rem(14),
						},
					}
				},
			}),
		}),
	},
})

const theme: MantineTheme = mergeMantineTheme(DEFAULT_THEME, themeOverride)

export default theme
